import type { components } from '@connected/api-client/api.generated';

import { useEffect, useMemo } from 'react';

import type { LoginOptions } from '../lib/login';

import { me } from '../data/auth/me';
import { isTokenExpired, removeAuthentication } from '../lib/authentication';
import { navigateToLogin } from '../lib/login';
import { useLogger } from './use-logger';

export type User = components['schemas']['RetrieveAccountInfoQueryResultInfo'];

/**
 *  A hook that returns the current authentication state.
 *
 *  There are three possible states for `isAuthenticated`:
 *  - `null` - The authentication state is still loading.
 *  - `false` - The user is not authenticated.
 *  - `true` - The user is authenticated.
 *    - `currentUser` - The current user object.
 */
export type AuthenticationContext<T extends boolean | null> = T extends true
    ? { isAuthenticated: true; currentUser: User }
    : T extends false
        ? { isAuthenticated: false; currentUser: null }
        : { isAuthenticated: true; currentUser: User };

export function useAuthentication<T extends boolean | null>({
    redirectToLogin = false,
    onBeforeLoginRedirect,
}: {
    redirectToLogin?: boolean;
    /**
     * Optional callback called before redirecting to login.
     * Return `true` to proceed with redirect, `false` to cancel.
     *
     * Use this to show sync modals, check for pending updates, etc.
     *
     * @example
     * useAuthentication({
     *     redirectToLogin: true,
     *     onBeforeLoginRedirect: async () => {
     *         if (hasPendingMutations()) {
     *             showSyncModal();
     *             return false; // Don't redirect yet
     *         }
     *         return true; // OK to redirect
     *     },
     * });
     */
    onBeforeLoginRedirect?: LoginOptions['onBeforeRedirect'];
} = {}): AuthenticationContext<T> {
    const { logger } = useLogger();
    const { data } = me.useSuspenseQuery();

    const currentUser = useMemo(() => data ?? null, [data]);
    const isAuthenticated = useMemo(() => currentUser !== null, [currentUser]);

    useEffect(() => {
        if (!redirectToLogin || isAuthenticated) {
            return;
        }

        if (isTokenExpired() === 'EXPIRED') {
            logger.error({
                system: 'Authentication',
                message: 'Token expired',
                tokenStatus: isTokenExpired(),
            });
        }
        else {
            logger.error({
                system: 'Authentication',
                message: 'User not authenticated',
            });
        }

        removeAuthentication();

        navigateToLogin({
            onBeforeRedirect: onBeforeLoginRedirect,
        });
    }, [redirectToLogin, onBeforeLoginRedirect]);

    return isAuthenticated
        ? ({ isAuthenticated: true, currentUser } as AuthenticationContext<T>)
        : ({ isAuthenticated: false, currentUser: null } as AuthenticationContext<T>);
}
